/**
 * Company / field events API endpoints.
 */

import { XLSX_MIME } from '@/constants/mime-types.constants';
import { apiClient } from './api-client';
import type {
  EventWithParticipants,
  EventParticipant,
  EventUnregisteredParticipant,
  CreateEventDto,
  UpdateEventDto,
  CancelEventDto,
  ListEventsQuery,
  UpdateEventParticipantDto,
  UpdateEventUnregisteredParticipantDto,
  EventListItem,
  EventsResponse,
} from '@/types/event.types';

/** Events REST client (`/events` CRUD, attendance patches, export). */
class EventsService {
  /** `GET /events` */
  async getEvents(params: ListEventsQuery = {}): Promise<EventsResponse> {
    const { data } = await apiClient.get<{ events: EventListItem[]; total: number }>('/events', {
      params,
    });
    return {
      data: data.events || [],
      total: data.total || 0,
    };
  }

  /** `GET /events/:id` */
  async getEvent(id: string): Promise<EventWithParticipants> {
    const { data } = await apiClient.get<EventWithParticipants>(`/events/${id}`);
    return data;
  }

  /** `POST /events` */
  async createEvent(eventDto: CreateEventDto): Promise<EventWithParticipants> {
    const { data } = await apiClient.post<EventWithParticipants>('/events', eventDto);
    return data;
  }

  /** `PATCH /events/:id` */
  async updateEvent(id: string, eventDto: UpdateEventDto): Promise<EventWithParticipants> {
    const { data } = await apiClient.patch<EventWithParticipants>(`/events/${id}`, eventDto);
    return data;
  }

  /** `POST /events/:id/cancel` */
  async cancelEvent(id: string, cancelDto: CancelEventDto): Promise<EventWithParticipants> {
    const { data } = await apiClient.post<EventWithParticipants>(`/events/${id}/cancel`, cancelDto);
    return data;
  }

  /** `GET /events/export` — download XLSX. */
  async exportEvents(params: ListEventsQuery = {}): Promise<Blob> {
    const { data } = await apiClient.get<Blob>('/events/export', {
      params,
      responseType: 'blob',
      headers: { Accept: XLSX_MIME },
    });
    return data;
  }

  /** `PATCH /events/:eventId/participants/:participantId` */
  async updateEventParticipant(
    eventId: string,
    participantId: string,
    dto: UpdateEventParticipantDto,
  ): Promise<EventParticipant> {
    const { data } = await apiClient.patch<EventParticipant>(
      `/events/${eventId}/participants/${participantId}`,
      dto,
    );
    return data;
  }

  /** `PATCH /events/:eventId/unregistered-participants/:participantId` */
  async updateUnregisteredParticipant(
    eventId: string,
    participantId: string,
    dto: UpdateEventUnregisteredParticipantDto,
  ): Promise<EventUnregisteredParticipant> {
    const { data } = await apiClient.patch<EventUnregisteredParticipant>(
      `/events/${eventId}/unregistered-participants/${participantId}`,
      dto,
    );
    return data;
  }
}

/** Events API (`/events` CRUD, attendance patches, export). */
export const eventsService = new EventsService();
